import { useState, useEffect } from 'react';
import {
  getPricingConfigs,
  putPricing,
  getMachines,
  type PricingConfig,
  type Machine,
} from '../api/client';

const DEFAULTS: Omit<PricingConfig, 'machineId'> = {
  bwPageCzk: 3,
  colorPageCzk: 8,
  minJobCzk: 15,
  cardThresholdCzk: 50,
  coinChangeReserveCzk: 45,
  coinSmallTubeMin: 5,
  billMaxCzk: 200,
  billRefundGuard: true,
};

interface EditorProps {
  machineId: number | null;
  initial: Omit<PricingConfig, 'machineId'>;
  note?: string;
  onSaved: () => void;
}

function PriceEditor({ machineId, initial, note, onSaved }: EditorProps) {
  const [bw, setBw] = useState(String(initial.bwPageCzk));
  const [color, setColor] = useState(String(initial.colorPageCzk));
  const [min, setMin] = useState(String(initial.minJobCzk));
  const [card, setCard] = useState(String(initial.cardThresholdCzk));
  const [reserve, setReserve] = useState(String(initial.coinChangeReserveCzk ?? DEFAULTS.coinChangeReserveCzk));
  const [tubeMin, setTubeMin] = useState(String(initial.coinSmallTubeMin ?? DEFAULTS.coinSmallTubeMin));
  const [billMax, setBillMax] = useState(String(initial.billMaxCzk ?? DEFAULTS.billMaxCzk));
  const [refundGuard, setRefundGuard] = useState(initial.billRefundGuard ?? DEFAULTS.billRefundGuard ?? true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      await putPricing({
        machineId,
        bwPageCzk: parseInt(bw, 10),
        colorPageCzk: parseInt(color, 10),
        minJobCzk: parseInt(min, 10),
        cardThresholdCzk: parseInt(card, 10),
        coinChangeReserveCzk: parseInt(reserve, 10),
        coinSmallTubeMin: parseInt(tubeMin, 10),
        billMaxCzk: parseInt(billMax, 10),
        billRefundGuard: refundGuard,
      });
      setMsg('Saved ✓');
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  const num = (v: string, set: (s: string) => void, label: string) => (
    <div className="form-group" style={{ margin: 0 }}>
      <label style={{ fontSize: 12 }}>{label}</label>
      <input
        className="form-input"
        type="number"
        min={0}
        step={1}
        value={v}
        onChange={(e) => set(e.target.value)}
        disabled={busy}
        style={{ width: 90 }}
      />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {num(bw, setBw, 'Č/B Kč/стр')}
        {num(color, setColor, 'Цвет Kč/стр')}
        {num(min, setMin, 'Мин. заказ Kč')}
        {num(card, setCard, 'Порог карты Kč')}
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {num(reserve, setReserve, 'Запас сдачи для монет Kč')}
        {num(tubeMin, setTubeMin, 'Мин. монет в тубах 1/2 Kč')}
        {num(billMax, setBillMax, 'Макс. купюра Kč')}
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 8 }}>
          <input
            type="checkbox"
            checked={refundGuard}
            onChange={(e) => setRefundGuard(e.target.checked)}
            disabled={busy}
          />
          Купюра только если тубы могут её вернуть
        </label>
        <button className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? '...' : 'Сохранить'}
        </button>
        {note && <span className="text-muted" style={{ fontSize: 12 }}>{note}</span>}
        {msg && <span style={{ color: '#16a34a', fontSize: 12 }}>{msg}</span>}
        {err && <span className="login-error" style={{ margin: 0 }}>{err}</span>}
      </div>
      <div className="text-muted" style={{ fontSize: 11 }}>
        Наличные: монеты принимаются, если в тубах 1 и 2 Kč не меньше минимума и из туб собирается сдача
        «самая крупная монета − остаток к оплате», но не больше запаса (0 = не проверять). Купюра крупнее максимума
        отбивается; с включённой галочкой отбивается и купюра, которую тубы не смогли бы вернуть при отмене.
        Киоск подхватывает изменения в течение 10 минут без перезагрузки.
      </div>
    </div>
  );
}

export function PricingPage() {
  const [configs, setConfigs] = useState<PricingConfig[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [c, m] = await Promise.all([getPricingConfigs(), getMachines()]);
      setConfigs(c);
      setMachines(m);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const global = configs.find((c) => c.machineId == null);
  const globalVals = global ?? { machineId: null, ...DEFAULTS };
  const byMachine = new Map<number, PricingConfig>();
  configs.forEach((c) => {
    if (c.machineId != null) byMachine.set(c.machineId, c);
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2>Pricing</h2>
      </div>

      <p className="text-muted" style={{ marginBottom: 16 }}>
        Цены в целых Kč. Глобальные применяются к вебу и как фолбэк для киосков без своей строки.
        Per-machine переопределяет глобальные для конкретного киоска.
      </p>

      {loading && <div className="empty-state"><p className="text-muted">Loading...</p></div>}
      {error && <div className="login-error">{error}</div>}

      {!loading && (
        <>
          <div className="card mb-6">
            <div className="card-header">Глобальные (default)</div>
            <div className="card-body">
              <PriceEditor
                key={`global-${global?.updatedAt ?? 'none'}`}
                machineId={null}
                initial={globalVals}
                note={global ? undefined : 'нет строки — сохранение создаст глобальную'}
                onSaved={load}
              />
            </div>
          </div>

          <div className="card">
            <div className="card-header">Per-machine ({machines.length} машин)</div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {machines.length === 0 && <p className="text-muted">Нет машин.</p>}
              {machines.map((m) => {
                const ov = byMachine.get(m.machineId);
                return (
                  <div key={m.machineId} style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>
                      #{m.machineId} {m.name || ''}{' '}
                      <span className="text-muted" style={{ fontWeight: 400, fontSize: 12 }}>
                        {ov ? '(своя цена)' : '(наследует глобальные)'}
                      </span>
                    </div>
                    <PriceEditor
                      key={`m-${m.machineId}-${ov?.updatedAt ?? 'global'}`}
                      machineId={m.machineId}
                      initial={ov ?? globalVals}
                      onSaved={load}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
